import { createContext, useContext, useState, useRef, useEffect, useCallback, useMemo } from "react";
import PopupComponent from "../components/PopupComponent";

const PopupContext = createContext();
const popup_delay_s = 5;
export const PopupProvider = ({ children }) => {
  const [popup, setPopup] = useState(null);
  const logoutTimerRef = useRef(null);

  const showPopup = useCallback((message, type = "info", options = {}) => {
    if (type === "logout") {
      setPopup({
        message: `Logging out in ${options.countdown || 10} seconds...`,
        type,
        countdown: options.countdown || 10,
        onClose: options.onClose // Pass the onClose callback from options
      });

      let countdown = options.countdown || 10;
      logoutTimerRef.current = setInterval(() => {
        countdown -= 1;
        if (countdown > 0) {
          setPopup((prev) => ({ ...prev, message: `Logging out in ${countdown} seconds...`, countdown }));
        } else {
          clearInterval(logoutTimerRef.current);
          logoutTimerRef.current = null;
          setPopup({ message: "Logged out successfully", type: "success", duration: 2000, autoDismiss: true });
        }
      }, 1000);
    } else {
      setPopup({ message, type, ...options });
      if (options.autoDismiss !== false) {
        setTimeout(() => setPopup(null), options.duration || popup_delay_s*1000);
      }
    }
  }, []);

  const closePopup = useCallback(() => {
    // Use setPopup with function to access current popup state without dependency
    setPopup((currentPopup) => {
      // Call the onClose callback if it exists (for logout popups)
      if (currentPopup && currentPopup.onClose) {
        currentPopup.onClose();
      }
      return null;
    });

    // Clear the countdown interval
    if (logoutTimerRef.current) {
      clearInterval(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
  }, []); // No dependencies - stable reference

  useEffect(() => {
    return () => {
      if (logoutTimerRef.current) clearInterval(logoutTimerRef.current);
    };
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    showPopup,
    closePopup
  }), [showPopup, closePopup]);

  return (
    <PopupContext.Provider value={contextValue}>
      {children}
      {popup && <PopupComponent {...popup} closePopup={closePopup} />}
    </PopupContext.Provider>
  );
};

export const usePopup = () => useContext(PopupContext);